import { isEmpty } from 'lodash';
import React, { useEffect } from 'react';
import { useInView } from '../../../hooks/useInView';
import { useSearchResultsQuery } from '../../../queries/ats/titles/hooks';
import { SectionContextProvider } from '../../../views/ats-pages/section/section-context';
import { AtsAsset } from '../../ATS/ats-asset';
import { Grid } from '../../ATS/Grid/Grid';
import Spinner from '../../Spinner';
import NoResultsPlacement from './NoResultPlacement';
import styles from './search-results.module.scss';
import SearchResultViewItem from './SearchResultViewItem';

interface SearchResultsProps {
  query: string;
}

function SearchResults({ query }: SearchResultsProps) {
  const { data, fetchNextPage, hasNextPage, isLoading, isIdle, isFetchingNextPage } = useSearchResultsQuery(query);

  const { ref, inView } = useInView<HTMLDivElement>({ root: null, rootMargin: '750px', threshold: 0 });

  useEffect(() => {
    if (inView && hasNextPage) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, inView]);

  if (isIdle) {
    return <NoResultsPlacement query={query} />;
  }

  if (isLoading) {
    return <Spinner wrapInContainer={true} />;
  }

  if (data === undefined) {
    throw new Error('Could not load search results');
  }

  const items = data.pages
    .map((page) => page.data)
    .flat()
    .filter((x) => x);

  if (isEmpty(items)) {
    return <NoResultsPlacement query={query} />;
  }

  return (
    <main className={styles.container} key={'results'}>
      <SectionContextProvider
        // We fake a continue watching section so we have the progress bar on the search results page
        section={{ label: 'Search', link: '', slug: 'search', style: { pattern: 'wide', type: 'continue-watching' } }}
      >
        <Grid
          children={items.map((item) => (
            <SearchResultViewItem
              key={item.value.masterId}
              title={item.value}
              highlights={item.highlights}
              query={query}
            />
          ))}
        />

        {isFetchingNextPage && (
          <Grid
            children={Array.from({ length: 5 }).map((_, index) => (
              <AtsAsset.Skeleton key={index} />
            ))}
          />
        )}
      </SectionContextProvider>

      {items.length === 0 && <NoResultsPlacement query={query} />}

      <div ref={ref} className={styles.end_of_results} />
    </main>
  );
}

export default SearchResults;
